'use client';

import { cn } from '@core/utils';
import type { DetailHomeGiftProductResponse } from '@generated/unauth';
import { useRef } from 'react';

import { usePublishElementHeight } from '@/hooks/usePublishElementHeight';

import { useGiftOption } from '../_context/GiftOptionContext';
import { AddToCartButton } from './AddToCartButton';

interface MobileAddToCartFooterProps {
  productNumber: string;
  productInfo: DetailHomeGiftProductResponse;
}

export const MobileAddToCartFooter = ({
  productNumber,
  productInfo,
}: MobileAddToCartFooterProps) => {
  const { selectedOptions, noOptionQuantity, getCartOptions } = useGiftOption();
  const footerRef = useRef<HTMLElement>(null);

  usePublishElementHeight(footerRef, '--bottom-nav-portal-height');

  const optionGroups = productInfo.optionGroupInfoList ?? [];
  const hasOptions = optionGroups.length > 0;
  const requiredOptionGroupIds = optionGroups
    .filter((g) => g.requiredYN)
    .map((g) => g.optionGroupId);

  const scrollToOptionSection = () => {
    document
      .querySelector<HTMLElement>('[data-gift-option-section]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // 필수 옵션 그룹을 모두 선택했는지 확인
  const requiredOptionGroups = optionGroups.filter((g) => g.requiredYN);
  const isAllRequiredOptionsSelected =
    hasOptions &&
    requiredOptionGroups.every((g) =>
      selectedOptions.has(g.optionGroupId ?? 0),
    );
  const selectedQuantity = (() => {
    if (!isAllRequiredOptionsSelected || optionGroups.length === 0) return 1;
    const firstGroupId = optionGroups[0]?.optionGroupId ?? 0;

    return selectedOptions.get(firstGroupId)?.quantity ?? 1;
  })();

  return (
    <footer
      ref={footerRef}
      className={cn(
        'sticky bottom-0 z-10 hidden w-full bg-Gray-white px-20 py-12 pb-[calc(12px+var(--safe-area-bottom,0px))]',
        'mobile-tablet:flex mobile-tablet:items-center mobile-tablet:gap-8 mobile-tablet:shadow-lg',
      )}
    >
      <AddToCartButton
        className="flex-1"
        productNumber={productNumber}
        productInfo={productInfo}
        hasOptions={hasOptions}
        selectedOptions={getCartOptions(optionGroups)}
        quantity={hasOptions ? selectedQuantity : noOptionQuantity}
        requiredOptionGroupIds={requiredOptionGroupIds}
        onOptionNotSelected={scrollToOptionSection}
      />
    </footer>
  );
};
